// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\components\LegalDocuments.js
//
// Privacy Policy · Terms & Conditions · Cancellation & Refund Policy ·
// Service Delivery Policy · Contact Us (28 Sep 2026; 1 Oct 2026).
//   LegalLinksBar      — the row of links at the bottom of the
//                        "Who are you registering for?" page. Each policy
//                        opens in a pop-up (registration is not lost);
//                        Contact Us opens the verify-email form (moved here
//                        from the landing page).
//   LegalDocumentPage  — the same text as a public page:
//                        /privacy-policy, /terms-and-conditions,
//                        /refund-policy, /service-delivery-policy,
//                        /contact-us (the payment gateway's website check
//                        needs these to be reachable without logging in).
//   LegalFooter        — home-page footer: links to all five pages and
//                        "ThyroConsult™ is operated by …" (1 Oct 2026).
// The wording lives in content/legalDocuments.js.
//
// Hindi (13 Oct 2026): on a Hindi page only the LINK NAMES are in Hindi
// (footer and links bar). Each policy itself still opens in English —
// the English text is the binding one; a Hindi text may be added later.
// ============================================================

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { LEGAL_DOCUMENTS, LEGAL_LAST_UPDATED, OPERATOR } from '../content/legalDocuments';
import { ContactUsModal } from './ContactUs';
import { MascotLogo } from './common/ThyroDoctorMascot';
import { useI18n } from '../i18n'; // link names in Hindi (13 Oct 2026)

// **bold** and [Insert …] placeholders inside one line of text.
function inline(text, keyBase) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\[Insert[^\]]*\])/g).filter(Boolean);
  return parts.map((p, i) => {
    const markStyle = { background: '#fef3c7', color: '#92400e', padding: '0 3px', borderRadius: 3 };
    if (p.startsWith('**') && p.endsWith('**')) {
      const inner = p.slice(2, -2);
      // a bold placeholder is highlighted too (29 Sep 2026)
      return <strong key={`${keyBase}-${i}`}>{inner.startsWith('[Insert') ? <mark style={markStyle}>{inner}</mark> : inner}</strong>;
    }
    if (p.startsWith('[Insert')) return <mark key={`${keyBase}-${i}`} style={markStyle}>{p}</mark>;
    return <React.Fragment key={`${keyBase}-${i}`}>{p}</React.Fragment>;
  });
}

// '#### ' heading · '* ' bullet · '> ' quoted line · blank line = new paragraph
export function LegalText({ body }) {
  const blocks = [];
  let para = [];
  let list = [];
  const flushPara = () => { if (para.length) { blocks.push({ t: 'p', lines: para }); para = []; } };
  const flushList = () => { if (list.length) { blocks.push({ t: 'ul', items: list }); list = []; } };
  String(body || '').split('\n').forEach(raw => {
    const line = raw.replace(/\s+$/, '');
    const s = line.trim();
    if (!s || s === '>') { flushPara(); flushList(); return; }
    if (s.startsWith('#### ')) { flushPara(); flushList(); blocks.push({ t: 'h', text: s.slice(5).replace(/^\*\*|\*\*$/g, '') }); return; }
    if (/^\*\s+/.test(s)) { flushPara(); list.push(s.replace(/^\*\s+/, '')); return; }
    flushList();
    para.push(s.replace(/^>\s?/, ''));
  });
  flushPara(); flushList();

  return (
    <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary, #1e293b)' }}>
      {blocks.map((b, i) => {
        if (b.t === 'h') return <h3 key={i} style={{ fontSize: 16, margin: '22px 0 8px' }}>{inline(b.text, i)}</h3>;
        if (b.t === 'ul') {
          return (
            <ul key={i} style={{ margin: '6px 0 12px', paddingLeft: 22 }}>
              {b.items.map((it, j) => <li key={j} style={{ margin: '4px 0' }}>{inline(it, `${i}-${j}`)}</li>)}
            </ul>
          );
        }
        return <p key={i} style={{ margin: '0 0 12px' }}>{b.lines.map((l, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(l, `${i}-${j}`)}</React.Fragment>)}</p>;
      })}
    </div>
  );
}

function LegalDocumentModal({ docKey, onClose }) {
  const doc = LEGAL_DOCUMENTS[docKey];
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!doc) return null;
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={doc.title}
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>{doc.title}</h2>
            <div style={{ fontSize: 12, color: '#64748b' }}>ThyroConsult™ · Last updated {LEGAL_LAST_UPDATED}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 22px 22px' }}>
          <LegalText body={doc.body} />
        </div>
      </div>
    </div>,
    document.body
  );
}

// Bottom of the "Who are you registering for?" page.
export function LegalLinksBar() {
  const { t } = useI18n();
  const [open, setOpen] = useState(null); // 'privacy' | 'terms' | 'refund' | 'delivery' | 'contact' | null
  const link = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--teal-600)', textDecoration: 'underline', cursor: 'pointer' };
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 18px', marginTop: 22, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 13 }}>
        <button type="button" style={link} onClick={() => setOpen('privacy')}>{t('Privacy Policy')}</button>
        <button type="button" style={link} onClick={() => setOpen('terms')}>{t('Terms & Conditions')}</button>
        <button type="button" style={link} onClick={() => setOpen('refund')}>{t('Cancellation & Refund Policy')}</button>
        <button type="button" style={link} onClick={() => setOpen('delivery')}>{t('Service Delivery Policy')}</button>
        <button type="button" style={link} onClick={() => setOpen('contact')}>{t('Contact Us')}</button>
      </div>
      {open && open !== 'contact' && <LegalDocumentModal docKey={open} onClose={() => setOpen(null)} />}
      {open === 'contact' && <ContactUsModal mode="public" onClose={() => setOpen(null)} />}
    </>
  );
}

// "ThyroConsult™ by INDIA THYROID CLINIC™" — home-page footer (6 Oct 2026).
const SupTM = () => <sup style={{ fontSize: 9, fontWeight: 700, verticalAlign: 'super', marginLeft: 1 }}>TM</sup>;

// The two brand names written in each website language, so every patient
// can read the footer in the language they chose (owner, 15 Oct 2026).
// Hindi wording given by the owner. English stays exactly as before.
// Each pair: [ThyroConsult, India Thyroid Clinic]; ™ is added on screen.
const BRAND_NAMES = {
  hi:   ['थायरोकंसल्ट', 'इंडिया थायरॉइड क्लिनिक'],
  mr:   ['थायरोकन्सल्ट', 'इंडिया थायरॉईड क्लिनिक'],
  ne:   ['थाइरोकन्सल्ट', 'इन्डिया थाइराइड क्लिनिक'],
  gu:   ['થાયરોકન્સલ્ટ', 'ઇન્ડિયા થાયરોઇડ ક્લિનિક'],
  pa:   ['ਥਾਇਰੋਕੰਸਲਟ', 'ਇੰਡੀਆ ਥਾਇਰਾਇਡ ਕਲੀਨਿਕ'],
  bn:   ['থাইরোকনসাল্ট', 'ইন্ডিয়া থাইরয়েড ক্লিনিক'],
  as:   ['থাইৰোকনচাল্ট', 'ইণ্ডিয়া থাইৰয়ড ক্লিনিক'],
  or:   ['ଥାଇରୋକନସଲ୍ଟ', 'ଇଣ୍ଡିଆ ଥାଇରଏଡ୍ କ୍ଲିନିକ'],
  ta:   ['தைரோகன்சல்ட்', 'இந்தியா தைராய்டு கிளினிக்'],
  te:   ['థైరోకన్సల్ట్', 'ఇండియా థైరాయిడ్ క్లినిక్'],
  kn:   ['ಥೈರೋಕನ್ಸಲ್ಟ್', 'ಇಂಡಿಯಾ ಥೈರಾಯ್ಡ್ ಕ್ಲಿನಿಕ್'],
  ml:   ['തൈറോകൺസൾട്ട്', 'ഇന്ത്യ തൈറോയ്ഡ് ക്ലിനിക്'],
  mnib: ['থাইরোকনসল্ত', 'ইন্দিয়া থাইরোইদ ক্লিনিক'],
  mnim: ['ꯊꯥꯢꯔꯣꯀꯟꯁꯜꯇ', 'ꯏꯟꯗꯤꯌꯥ ꯊꯥꯢꯔꯣꯢꯗ ꯀ꯭ꯂꯤꯅꯤꯛ'],
};
const brandNamesFor = (lang) => BRAND_NAMES[lang] || null;

function OperatedBy() {
  const { lang } = useI18n();
  const local = brandNamesFor(lang);
  if (local) {
    // "by" is left out on purpose — a dash reads correctly in every
    // language, whatever its word order.
    return (
      <div>
        {local[0]}<SupTM /> — {local[1]}<SupTM />
      </div>
    );
  }
  return (
    <div>
      ThyroConsult<SupTM /> by {OPERATOR.name}<SupTM />
    </div>
  );
}

// Home-page footer (1 Oct 2026): every policy one click from the home page.
export function LegalFooter() {
  const { t, lang } = useI18n();
  const local = brandNamesFor(lang);
  return (
    <footer style={{ borderTop: '1px solid var(--border)', padding: '24px 24px 28px', textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
      <nav aria-label={t('Policies')} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 18px', marginBottom: 14 }}>
        {Object.entries(LEGAL_DOCUMENTS).map(([k, d]) => (
          <Link key={k} to={d.path} style={{ color: 'var(--teal-600)' }}>{t(d.title)}</Link>
        ))}
      </nav>
      <OperatedBy />
      <div style={{ marginTop: 6 }}>
        © {new Date().getFullYear()} {local ? local[1] : OPERATOR.name}<SupTM />. {t('All rights reserved.')}
      </div>
    </footer>
  );
}

// Public page: /privacy-policy · /terms-and-conditions · /refund-policy ·
// /service-delivery-policy · /contact-us
export function LegalDocumentPage({ docKey }) {
  const doc = LEGAL_DOCUMENTS[docKey];
  const [messageOpen, setMessageOpen] = useState(false);
  useEffect(() => { if (doc) document.title = `${doc.title} — ThyroConsult™`; }, [doc]);
  if (!doc) return null;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50, #f8fafc)' }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '28px 18px 48px' }}>
        {/* Mascot logo header (4 Oct 2026) — both go to the home page */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Link to="/" aria-label="ThyroConsult home" style={{ textDecoration: 'none' }}>
            <MascotLogo height={40} fontSize={18} />
          </Link>
          <Link to="/" style={{ fontSize: 13 }}>← Home</Link>
        </div>
        <div className="card" style={{ marginTop: 12 }}>
          <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>{doc.title}</h1>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>ThyroConsult™ · Last updated {LEGAL_LAST_UPDATED}</div>
          <LegalText body={doc.body} />
          {docKey === 'contact' && (
            <button type="button" className="btn btn-primary" onClick={() => setMessageOpen(true)} style={{ marginTop: 4 }}>
              Send message
            </button>
          )}
        </div>
        {/* Bottom policy links and "operated by" line removed here (4 Oct
            2026) — the header's Home link is enough; both stay in the
            home-page footer (LegalFooter). */}
      </div>
      {messageOpen && <ContactUsModal mode="public" onClose={() => setMessageOpen(false)} />}
    </div>
  );
}
